import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Lightbulb, Vote, CheckCircle2 } from 'lucide-react';

interface WishlistModalProps {
  open: boolean;
  onClose: () => void;
}

export const WishlistModal = ({ open, onClose }: WishlistModalProps) => {
  const [submitted, setSubmitted] = useState(false);
  
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border text-foreground">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-6 h-6 text-primary" />
            <DialogTitle className="text-xl font-bold text-gradient">
              Shape the Future of chatMCP
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            We build for you. Vote for features or suggest new ones to help us prioritize our roadmap.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-10 space-y-4 text-center">
            <div className="rounded-full bg-green-500/20 p-4">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <h3 className="text-xl font-bold">Feedback Received!</h3>
            <p className="text-muted-foreground max-w-xs">
              Thank you for helping us improve. We review every suggestion carefully.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 p-3 rounded-lg border border-border/50 flex items-start space-x-3">
                <Lightbulb className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Idea Submission</p>
                  <p className="text-xs text-muted-foreground">Suggest new capabilities</p>
                </div>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg border border-border/50 flex items-start space-x-3">
                <Vote className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Vote on Roadmap</p>
                  <p className="text-xs text-muted-foreground">Influence priorities</p>
                </div>
              </div>
            </div>

            <form
              method="POST"
              action="#" 
              onSubmit={handleSubmit} 
              id="chatMCP_WishlistForm"
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="feature-request">What feature are you missing?</Label>
                <Textarea 
                  id="feature-request" 
                  name="feature-request"
                  placeholder="I wish chatMCP could..." 
                  className="min-h-[100px] bg-background border-border focus:border-primary"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email-contact">Email *</Label>
                <Input 
                  id="email-contact" 
                  name="email"
                  type="email" 
                  placeholder="name@company.com" 
                  className="bg-background border-border focus:border-primary"
                  required
                />
                <p className="text-xs text-muted-foreground">We'll only contact you if we need clarification on your idea.</p>
              </div>

              <div className="pt-2">
                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-2 rounded-lg shadow-lg transition-all duration-200 hover:scale-[1.02]"
                >
                  Send to Product Team
                </Button>
              </div>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
